import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MAX_RECENT_SEARCHES } from "../constants/globalSearch.constants";

interface GlobalSearchState {
  isOpen: boolean;
  query: string;
  keywordOnly: boolean;
  selectedIndex: number;
  recentSearches: string[];
}

interface GlobalSearchActions {
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  setQuery: (query: string) => void;
  setKeywordOnly: (keywordOnly: boolean) => void;
  setSelectedIndex: (index: number) => void;
  addRecentSearch: (query: string) => void;
  clearRecentSearches: () => void;
  reset: () => void;
}

type GlobalSearchStore = GlobalSearchState & GlobalSearchActions;

export const useGlobalSearchStore = create<GlobalSearchStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: false,
      query: "",
      keywordOnly: false, // Default: use RRF hybrid search
      selectedIndex: -1, // -1 means no selection (prevents accidental Enter navigation)
      recentSearches: [],

      // Actions
      openSearch: () => set({ isOpen: true, selectedIndex: -1 }),

      closeSearch: () =>
        set({
          isOpen: false,
          query: "",
          selectedIndex: -1,
        }),

      toggleSearch: () => {
        const { isOpen } = get();
        if (isOpen) {
          get().closeSearch();
        } else {
          get().openSearch();
        }
      },

      setQuery: (query: string) => set({ query, selectedIndex: -1 }),

      setKeywordOnly: (keywordOnly: boolean) =>
        set({ keywordOnly, selectedIndex: -1 }),

      setSelectedIndex: (index: number) => set({ selectedIndex: index }),

      addRecentSearch: (query: string) => {
        const { recentSearches } = get();
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return;

        // Remove duplicate if exists, add to front
        const filtered = recentSearches.filter((s) => s !== trimmedQuery);
        const updated = [trimmedQuery, ...filtered].slice(
          0,
          MAX_RECENT_SEARCHES,
        );
        set({ recentSearches: updated });
      },

      clearRecentSearches: () => set({ recentSearches: [] }),

      reset: () =>
        set({
          isOpen: false,
          query: "",
          keywordOnly: false,
          selectedIndex: -1,
        }),
    }),
    {
      name: "global-search-storage",
      // Only persist these fields
      partialize: (state) => ({
        keywordOnly: state.keywordOnly,
        recentSearches: state.recentSearches,
      }),
    },
  ),
);
